import store from 'store2';
import { ClientResponse, SubscribeResult } from '../typings';
import { Environment } from '../managers/Environment';
import { Logger } from '../helpers/Logger';
import { Database } from '../helpers/Database';
import { NetworkRequest } from '../helpers/NetworkRequest';
import { PermissionManager } from '../managers/PermissionManager';
import { ServiceWorkerManager } from '../managers/ServiceWorkerManager';
import { SubscriptionManager } from '../managers/SubscriptionManager';
import { AdapterConfig } from '../models/AdapterConfig';
import { SUBSCRIPTION_STATES } from '@/src/helpers/constants';
import { SidetalkLocalStorage, SubscriptionData, WebPushState } from '@/src/typings';

export class WebPush {
    state: WebPushState | null;
    pushButton: HTMLButtonElement | null;
    subscription: PushSubscription | null;
    config: AdapterConfig;

    constructor(config = {}) {
        this.state = null;
        this.pushButton = null;
        this.subscription = null;

        this.config = new AdapterConfig({ ...config });
    }

    setDebug = (isDebug: boolean): void => {
        Logger.setLevel(isDebug ? 'debug' : null);
    };

    init = async (options = {}): Promise<WebPush> => {
        this.config.setProperties(options);

        try {
            this.initState();

            await ServiceWorkerManager.register(this.config);
            this.setPermissionStateChanged(PermissionManager.getNotificationPermission());

            await this.setInitialSubscription();

            return this;
        } catch (error) {
            Logger.error('[Sidetalk][Push][API]: error on init:', error);
            return Promise.reject(error);
        }
    };

    initState = (): boolean => {
        if (!Environment.supportsServiceWorkers()) {
            Logger.error('[Sidetalk][Push][API]: Service worker not available on this browser');
            this.setState(SUBSCRIPTION_STATES.UNSUPPORTED, 'Service worker not available on this browser');

            throw new Error('Service worker not available on this browser');
        }

        if (!Environment.supportsPushManager()) {
            Logger.error('[Sidetalk][Push][API]: PushManager not available on this browser');
            this.setState(SUBSCRIPTION_STATES.UNSUPPORTED, 'PushManager not available on this browser');

            throw new Error('PushManager API not available on this browser');
        }

        // Check the current Notification permission.
        // If its denied, it's a permanent block until the user changes the permission
        if (PermissionManager.isDenied()) {
            Logger.warn('[Sidetalk][Push][API]: Sidetalk Push: The user has blocked notifications.');
            this.setState(SUBSCRIPTION_STATES.PERMISSION_DENIED, 'User denied permission');

            throw new Error('User denied notifications permission');
        }

        this.pushButton = document.getElementById('sidetalk-push-button') as HTMLButtonElement;
        if (this.pushButton) {
            this.pushButton.addEventListener('click', () => {
                if (this.state?.pushEnabled) {
                    this.unsubscribe();
                } else {
                    this.subscribe();
                }
            });
        }

        return true;
    };

    setState = (state: WebPushState, data?: unknown): void => {
        this.state = state;

        if (this.pushButton) {
            if (typeof state.interactive !== 'undefined') {
                if (state.interactive) {
                    this.pushButton.disabled = false;
                } else {
                    this.pushButton.disabled = true;
                }
            }

            if (typeof state.pushEnabled !== 'undefined') {
                if (state.pushEnabled) {
                    this.pushButton.textContent = 'Disable Push Messages';
                } else {
                    this.pushButton.textContent = 'Enable Push Messages';
                }
            }
        }

        try {
            this.config.onStateChange(state, data);
        } catch (error) {
            Logger.error('[Sidetalk][Push][API]: custom state change callback error', error);
        }
    };

    setInitialSubscription = async (): Promise<PushSubscription | null> => {
        try {
            const subscription: PushSubscription | null = await SubscriptionManager.getCurrentSubscription();

            if (!subscription) {
                // We aren't subscribed to push, so set UI to allow the user to enable push
                this.subscription = null;
                this.setState(SUBSCRIPTION_STATES.UNSUBSCRIBED);

                try {
                    this.config.onSubscriptionChange(null);
                } catch (error) {
                    Logger.error('[Sidetalk][Push][API]: custom subscription update callback error', error);
                }

                return Promise.resolve(null);
            }

            this.subscription = subscription;
            this.setState(SUBSCRIPTION_STATES.SUBSCRIBED);

            this.updateLocalStorage(true, this.getSubscriptionData(subscription));

            try {
                this.config.onSubscriptionChange(subscription);
            } catch (error) {
                Logger.error('[Sidetalk][Push][API]: custom subscription update callback error', error);
            }

            return Promise.resolve(subscription);
        } catch (error) {
            Logger.warn('[Sidetalk][Push][API]: Init subscription failed: ', error);
            return Promise.reject(error);
        }
    };

    setPermissionStateChanged = (permissionState: string | undefined): void => {
        switch (permissionState) {
            case 'denied':
                this.setState(SUBSCRIPTION_STATES.PERMISSION_DENIED);
                break;
            case 'granted':
                this.setState(SUBSCRIPTION_STATES.PERMISSION_GRANTED);
                break;
            case 'default':
                this.setState(SUBSCRIPTION_STATES.PERMISSION_PROMPT);
                break;
            default:
                Logger.error('[Sidetalk][Push][API]: Unexpected permission state: ', permissionState);
                break;
        }
    };

    getNotificationPermissionState = async (): Promise<string> => {
        if (navigator.permissions) {
            return (await navigator.permissions.query({ name: 'notifications' })).state;
        }

        return new Promise((resolve) => {
            resolve(Notification.permission);
        });
    };

    subscribe = async (): Promise<PushSubscription | null> => {
        // Disable the button so it can't be changed while we process the permission request
        this.setState(SUBSCRIPTION_STATES.STARTING_SUBSCRIBE);

        try {
            const result: SubscribeResult | null = await SubscriptionManager.subscribe(this.config);

            this.subscription = result?.subscription ?? null;

            if (this.subscription) {
                this.setState(SUBSCRIPTION_STATES.SUBSCRIBED);
                // Send the subscription.endpoint to server
                // and save it on local storage to persistent push notification info
                await this.registerSubscription(this.subscription);
            }

            return Promise.resolve(this.subscription);
        } catch (subscriptionErr) {
            Logger.error('[Sidetalk][Push][API]: Unable to subscribe to push.', subscriptionErr);
            this.setState(SUBSCRIPTION_STATES.ERROR, subscriptionErr);

            const permission: NotificationPermission | undefined = PermissionManager.getNotificationPermission();
            if (PermissionManager.isDenied(permission)) {
                // The user denied the notification permission which means we failed to subscribe and the user will
                // need to manually change the notification permission to subscribe to push messages.
                Logger.warn('[Sidetalk][Push][API]: Permission for Notifications was denied', subscriptionErr);
            }

            if (permission) {
                this.setPermissionStateChanged(permission);
            }

            return Promise.reject(subscriptionErr);
        }
    };

    unsubscribe = async (): Promise<void> => {
        // Disable the button so it can't be changed while we process permissions
        this.setState(SUBSCRIPTION_STATES.STARTING_UNSUBSCRIBE);

        try {
            await SubscriptionManager.unsubscribe(this.config);
        } catch (error) {
            Logger.error(
                '[Sidetalk][Push][API]: Error thrown while revoking push notifications. ' +
                    'Most likely because push was never registered',
                error,
            );
        }

        this.setState(SUBSCRIPTION_STATES.UNSUBSCRIBED);

        if (this.subscription) {
            this.unregisterSubscription(this.subscription);
        }

        this.subscription = null;
    };

    getClientData = (): Record<string, unknown> => ({});

    getVisitorData = (): Record<string, unknown> => ({});

    getPlatform = (): string => 'web';

    getSubscriptionData = (subscription?: PushSubscription | null): SubscriptionData => ({
        adapter: this.config.adapter,
        client: this.getClientData(),
        visitor: this.getVisitorData(),
        platform: this.getPlatform(),
        subscription: subscription ? SubscriptionManager.parseRawSubscription(subscription) : null,
        action: subscription ? 'register' : 'unregister',
        contactlistId: this.config.contactlistId,
    });

    registerSubscription = async (subscription?: PushSubscription | null): Promise<ClientResponse> => {
        const updateURL: string = subscription ? this.config.getAPIRegisterURL() : this.config.getAPIUnregisterURL();
        const data: SubscriptionData = this.getSubscriptionData(subscription);

        return new Promise((resolve, reject) => {
            NetworkRequest.post(updateURL, data)
                .then((response) => {
                    this.updateLocalStorage(true, data);

                    Logger.debug('[Sidetalk][Push][API]: Successful registration.', data, data.subscription);

                    try {
                        this.config.onSubscriptionChange(subscription);
                        resolve(response);
                    } catch (error) {
                        Logger.error('[Sidetalk][Push][API]: custom subscription update callback error', error);
                        reject(error);
                    }
                })
                .catch((error) => {
                    this.setState(SUBSCRIPTION_STATES.UNSUBSCRIBED);
                    reject(error);
                });
        });
    };

    unregisterSubscription = async (subscription?: PushSubscription | null): Promise<ClientResponse> => {
        const updateURL: string = this.config.getAPIUnregisterURL();
        const data: SubscriptionData = this.getSubscriptionData(subscription);

        this.updateLocalStorage(false, null);

        try {
            this.config.onSubscriptionChange(null);
        } catch (error) {
            Logger.error('[Sidetalk][Push][API]: custom subscription update callback error', error);
        }

        return NetworkRequest.post(updateURL, data);
    };

    updateLocalStorage = (subscribed: boolean, data: SubscriptionData | null): void => {
        const sidetalk: SidetalkLocalStorage = store.get('sidetalk', {});
        sidetalk.pn = sidetalk.pn || {};
        sidetalk.pn.subscribed = subscribed;
        sidetalk.pn.data = data;
        store.add('sidetalk', sidetalk);

        Database.set('subscribed', subscribed);
        Database.set('subscriptionData', data);
    };
}
